/**
 * Feature keys are the third permission layer (GBV-DEV-2026-001 Section 6).
 * A route guarded by requireFeature('dialer') is unreachable unless an admin
 * has granted that key on the caller's Membership for the active org.
 *
 * Read-only variants exist where the capability matrix says "view" — that is
 * how a Team Lead sees invoices without being able to raise one.
 */
export const FEATURE_KEYS = [
    'crm',
    'dataImport',
    'dataExport',
    'dialer',
    'callRecordings',
    'whatsappInbox',
    'whatsappSend',
    'waBroadcast',
    'socialMedia',
    'contentCalendar',
    'approvals',
    'invoicing',
    'invoicingView',
    'payments',
    'expenses',
    'dailyReport',
    'reportSend',
    'assets',
    'confidentialAssets',
    'tasks',
    'appointments',
    'analytics',
    'hr',
    'integrations',
    'auditLog',
    'aiAssistant',
    /*
     * Counterparty verification — running due diligence on a business before
     * dealing with it.
     *
     * Deliberately absent from every role default, admin roles included by
     * virtue of holding all of them. It searches the open web from the
     * platform's own address, and a whole team running checks all day is how
     * that address gets rate-limited for everybody. More to the point, "is this
     * company real" is a question asked before money moves; it belongs with
     * whoever decides that, not with everyone who can open a lead.
     */
    'companyVerification',
    /*
     * Reading the record of what was removed.
     *
     * Separate from every delete key, and from auditLog, on purpose. Being
     * trusted to remove a task says nothing about being trusted to read back
     * what the whole team removed last month — that list carries the content of
     * deleted notes and mails, so it can expose more than the screens the
     * person is allowed to open. Absent from every role default.
     */
    'deletionHistory',
    /*
     * What a client may see of their own CRM, and whether they may change it.
     *
     * Four view keys rather than one, because "let the client into the CRM" is
     * not a single decision. An agency will happily show a client their own
     * contacts and not the lead pipeline they are still working, and the
     * granularity has to match that conversation or it gets answered with "no".
     *
     * `clientCrmEdit` is one switch on top rather than a write key per area:
     * a client trusted to correct a phone number is trusted to correct it
     * wherever they can already see it, and four more toggles would be four
     * more things to get wrong without expressing anything new.
     *
     * All absent from every role default, client roles included. A client
     * portal that showed the working pipeline by default would be a surprise
     * for every agency already using this, and the wrong surprise.
     */
    'clientCrmLeads',
    'clientCrmContacts',
    'clientCrmCompanies',
    'clientCrmDeals',
    'clientCrmEdit',
    /*
     * Removal is its own permission, separate from the feature it removes.
     *
     * Being able to work on something is not the same as being able to make it
     * cease to exist, and the two were the same key until now. These are absent
     * from every role default below SUB_ADMIN, so an ordinary employee cannot
     * delete anything until an admin grants the key for that area.
     */
    'deleteTasks',
    'deleteMessages',
    'deleteNotes',
    'deleteFiles',
    'deleteRecords',
    'deleteFinance',
    'deleteMarketing',
];
/**
 * Daily per-user counters enforced before the action runs.
 * A value of 0, null or undefined means unlimited.
 */
export const LIMIT_KEYS = [
    'maxCallsPerDay',
    'maxWaPerDay',
    'maxLeadsPerDay',
    'maxInvoicesPerDay',
    'maxBroadcastPerDay',
    'maxExportsPerDay',
    'maxAiPerDay',
];
/**
 * Categories for the shared file library. One list, used by the client portal,
 * the employee portal and the upload validator, so a folder name cannot drift
 * between them.
 */
export const ASSET_CATEGORIES = [
    'quotations',
    'catalogue',
    'profile',
    'invoices',
    // Daily report PDFs are filed here when a report is sent, so the client's
    // reports sit alongside their own documents rather than only in email.
    'reports',
    'posters',
    'design',
    'contracts',
    'brand',
    // Trademarks, registrations, and the paperwork that proves the brand is
    // theirs. Kept apart from 'brand' because one is the artwork and the other
    // is the claim to it.
    'ip',
    'other',
];
export const ASSET_CATEGORY_LABELS = {
    quotations: 'Quotations',
    catalogue: 'Catalogue',
    profile: 'Company profile',
    invoices: 'Invoices',
    reports: 'Daily reports',
    posters: 'Posters',
    design: 'Design material',
    contracts: 'Contracts',
    brand: 'Brand assets',
    ip: 'Intellectual property',
    other: 'Other',
};
export function isAssetCategory(value) {
    return ASSET_CATEGORIES.includes(value);
}
/**
 * What may be uploaded — GBV-DEV-2026-001 Section 13 requires an allowlist
 * rather than a blocklist, so a new dangerous type is denied by default.
 */
export const ALLOWED_UPLOAD_MIME = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/gif',
    'image/svg+xml',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'text/csv',
    'text/plain',
    'application/zip',
];
export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
export function isAllowedUploadMime(mime) {
    return ALLOWED_UPLOAD_MIME.includes(mime);
}
/**
 * The keys that mean anything on a client login.
 *
 * A membership can carry any key, but most of them govern screens the client
 * portal does not have: granting a client `dialer` or `auditLog` or
 * `deleteTasks` changes nothing they can see, and offering the toggle implies
 * otherwise. The admin console shows this list for a client and the full set
 * for staff, so the question an admin is answering — "what may this client
 * reach" — is the only one on screen.
 */
export const CLIENT_PORTAL_KEYS = [
    'clientCrmLeads',
    'clientCrmContacts',
    'clientCrmCompanies',
    'clientCrmDeals',
    'clientCrmEdit',
    'dailyReport',
    'approvals',
    'invoicingView',
    'assets',
    'analytics',
    'dataExport',
];
/**
 * The four CRM areas plus the edit switch, in the order the panel shows them.
 *
 * Kept apart from the rest because these are the ones an admin comes to change
 * — the others are set once when the client is created and rarely revisited.
 */
export const CLIENT_CRM_KEYS = [
    'clientCrmLeads',
    'clientCrmContacts',
    'clientCrmCompanies',
    'clientCrmDeals',
];
/** Said from the client's point of view, which is whose access it is. */
export const CLIENT_KEY_HINTS = {
    clientCrmLeads: 'Every enquiry in their book, with its timeline.',
    clientCrmContacts: 'The people they sell to.',
    clientCrmCompanies: 'The businesses those people belong to.',
    clientCrmDeals: 'Open and closed deals, with values.',
    clientCrmEdit: 'Correct a name, an email or a city in whatever they can see above. Every change is recorded against them.',
    dailyReport: 'The daily report and its archive.',
    approvals: 'Approve or reject work waiting on them.',
    invoicingView: 'Their invoices and payment status.',
    assets: 'The shared file library.',
    analytics: 'Charts and performance figures.',
    dataExport: 'Download their own records as a file.',
};
export function isFeatureKey(value) {
    return FEATURE_KEYS.includes(value);
}
export function isLimitKey(value) {
    return LIMIT_KEYS.includes(value);
}
/** Human labels for the admin portal's toggle list. */
export const FEATURE_LABELS = {
    crm: 'Leads — view, create, edit',
    dataImport: 'Import records from a file',
    dataExport: 'Export records to a file',
    dialer: 'Cloud dialer',
    callRecordings: 'Call recordings',
    whatsappInbox: 'WhatsApp inbox — read',
    whatsappSend: 'WhatsApp — send messages',
    waBroadcast: 'WhatsApp broadcast',
    socialMedia: 'Social media posts',
    contentCalendar: 'Content calendar',
    approvals: 'Approval queue',
    invoicing: 'Invoices — create and send',
    invoicingView: 'Invoices — view only',
    payments: 'Payments and reconciliation',
    expenses: 'Expense log',
    dailyReport: 'Daily report builder',
    reportSend: 'Send the daily report',
    assets: 'Client assets',
    confidentialAssets: 'Confidential assets',
    tasks: 'Task board',
    appointments: 'Appointments',
    analytics: 'Analytics dashboard',
    hr: 'People and HR',
    integrations: 'Integration manager',
    auditLog: 'Audit log viewer',
    aiAssistant: 'AI assistant',
    companyVerification: 'Company verification — due diligence checks',
    deletionHistory: 'Deleted items history — see what was removed',
    clientCrmLeads: 'Client portal — see their leads',
    clientCrmContacts: 'Client portal — see their contacts',
    clientCrmCompanies: 'Client portal — see their companies',
    clientCrmDeals: 'Client portal — see their deals',
    clientCrmEdit: 'Client portal — edit CRM records they can see',
    deleteTasks: 'Delete tasks, reminders and appointments',
    deleteMessages: 'Delete mails and chat messages',
    deleteNotes: 'Delete notes',
    deleteFiles: 'Delete files and documents',
    deleteRecords: 'Delete leads, contacts and companies',
    deleteFinance: 'Delete invoices, quotes and proposals',
    deleteMarketing: 'Delete social posts and websites',
};
export const LIMIT_LABELS = {
    maxCallsPerDay: 'Calls per day',
    maxWaPerDay: 'WhatsApp messages per day',
    maxLeadsPerDay: 'Leads created per day',
    maxInvoicesPerDay: 'Invoices per day',
    maxBroadcastPerDay: 'Broadcast recipients per day',
    maxExportsPerDay: 'Exports per day',
    maxAiPerDay: 'AI requests per day',
};
/**
 * What one audience may do with a file.
 *
 * Two levels per file — one for the client's own users, one for assigned
 * staff — because the interesting cases need both. A signed contract is READ
 * for everyone and WRITE for nobody. An internal margin sheet is NONE for the
 * client and WRITE for the team. Neither is expressible with a single flag.
 */
export const ASSET_ACCESS_LEVELS = ['NONE', 'READ', 'WRITE'];
export const ASSET_ACCESS_LABELS = {
    NONE: 'Hidden',
    READ: 'View and download',
    WRITE: 'Edit and replace',
};
/** Said from the file's point of view, for a line under a control. */
export const ASSET_ACCESS_HINTS = {
    NONE: 'They cannot see that this file exists.',
    READ: 'They can open and download it, but not change or remove it.',
    WRITE: 'They can rename, recategorise, replace and delete it.',
};
export function canRead(level) {
    return level !== 'NONE';
}
export function canWrite(level) {
    return level === 'WRITE';
}
//# sourceMappingURL=features.js.map